import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Provider as JotaiProvider } from "jotai";
import { Button } from '@material-ui/core';

/* eslint-disable no-unused-vars */
import FontAwesomeSetup from './utils/FontAwesomeSetup';
/* eslint-enable no-unused-vars */

import Fido from './utils/Fido';
import Constants from './utils/Constants';
import PendoHelper from "./utils/PendoHelper";
import IdGenerator from './utils/IdGenerator';
import AuthorizedUserContext from "./utils/AuthorizedUserContext";
import AuthorizedUser from "./utils/AuthorizedUser";
import CheckMaintenanceMode from "./utils/CheckMaintenanceMode";
import AppRouter from "./routes/AppRouter";
import FullScreen from './components/FullScreen';

// disable log if live (re-enabled below if ghosting)
if (Constants.ENV.IS_LIVE) {
	var originalConsole = {...console};
	console.log = console.info = console.warn = () => {};
}

class App extends Component {
	constructor(props) {
		super(props);

		this.messages = {
			accessDenied: "Access Denied",
			noAccess: "You do not have access to this page.",
			appUpdateAvailable: "App update available, please refresh.",
			networkOffline: "Your network is offline, cached data is being used.",
			somethingWrong: "Something went wrong",
			pageNotFound: "The page cannot be found."
		};

		// this used to be a ref in index.js, so by doing it this way we might lose some "component" features but currently not needed.
		// we changed it to this because using withRouter doesn't really work when using the ref, you lose the ref
		window.appRoot = this;

		this.bannerRef = React.createRef();
		this.snackbarInfo = {
			queue: [],
			queueProcessing: false,
			defaultDuration: 5000,
			currentConfig: null,
			snackbarRef: React.createRef()
		};

		this.updateUser = this.updateUser.bind(this);
		this.evictUser = this.evictUser.bind(this);
		this.loadUserSuccessful = this.loadUserSuccessful.bind(this);
		this.loadUserFailed = this.loadUserFailed.bind(this);
		this.logoutCallback = this.logoutCallback.bind(this);

		this.resetHeader = this.resetHeader.bind(this);
		this.updateHeader = this.updateHeader.bind(this);

		this.showSnackbar = this.showSnackbar.bind(this);
		this.updateSnackbarMessage = this.updateSnackbarMessage.bind(this);
		this.snackbarHandleEntering = this.snackbarHandleEntering.bind(this);
		this.snackbarHandleExited = this.snackbarHandleExited.bind(this);
		this.snackbarHandleClose = this.snackbarHandleClose.bind(this);

		this.showBanner = this.showBanner.bind(this);
		this.setBannerClassName = this.setBannerClassName.bind(this);
		this.bannerHandleEntering = this.bannerHandleEntering.bind(this);
		this.bannerHandleEntered = this.bannerHandleEntered.bind(this);
		this.bannerHandleExiting = this.bannerHandleExiting.bind(this);
		this.bannerHandleExited = this.bannerHandleExited.bind(this);

		this.updateNetworkStatus = this.updateNetworkStatus.bind(this);

		this.handleInstallBtn = this.handleInstallBtn.bind(this);
		this.handleInstallPrompt = this.handleInstallPrompt.bind(this);
		this.appRefreshSnackbar = this.appRefreshSnackbar.bind(this);
		this.iOSDlgCloseHandler = this.iOSDlgCloseHandler.bind(this);

		//this.signalsNudgeExited = this.signalsNudgeExited.bind(this);

		this.state = {
			csm: {},
			signInUrl: null,
			backUrl: null,
			title: null,
			pageTitle: Constants.PAGE_TITLE,
			primary: null,
			snackbarOpen: false,
			snackbarAction: null,
			snackbarMessage: null,
			snackbarDuration: null,
			banner: null,
			authorized: false,
			unauthenticated: false,
			hideHeaderbar: false,
			hideMargin: false,
			installable: false,
			showIOSInstallDlg: false,
			tallSnackbar: false,
			bannerClassName: null,
			bannerHeight: 0,
			previousLocation: null,
			//showUseSignalsBanner: true,
			showBackToBanner: true,
			//signalsNudgeBannerExited: false
		};
	}

	componentDidMount() {
		if(CheckMaintenanceMode.isDownForMaintenance((isDownForMaintenance) => {
			if(isDownForMaintenance){
				console.log('App is in Maintenance Mode');
				this.maintenanceMode();
			} else {
				if(!this.state.authorized) {
					AuthorizedUser.load((user)=>{
						this.loadUserSuccessful(user);
					}).catch(error => {
						this.loadUserFailed();
						console.log(error.message);
						//this.showSnackbar(error.message);
					});
	
				}
			}
		})) 

		window.addEventListener('online',  this.updateNetworkStatus);
		window.addEventListener('offline', this.updateNetworkStatus);
		window.addEventListener('beforeinstallprompt', this.handleInstallPrompt);
	}

	componentWillUnmount() {
		window.removeEventListener('online',  this.updateNetworkStatus);
		window.removeEventListener('offline', this.updateNetworkStatus);
		window.removeEventListener('beforeinstallprompt', this.handleInstallPrompt);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		// iOS only, initial load when user has to be authorized
		if (Constants.ENV.IS_IOS && this.state.authorized !== prevState.authorized && !window.navigator.standalone) {
			this.setState({showIOSInstallDlg: this.shouldShowIOSInstallDlg(), installable: true});
		}

		if (prevProps.location !== this.props.location) {
			this.onRouteChanged(prevProps);

			if (this.props.history.action !== "POP" && this.props.history.action !== "REPLACE") {
				this.resetScrollPosition();
			}
		}
	}

	onRouteChanged(prevProps) {
		this.setState({previousLocation: prevProps.location});
		if(this.isFocusResetNeeded(prevProps.location, this.props.location)) {
			this.resetFocus();
		}
	}

	isFocusResetNeeded = (prevLocation, location) => {
		// don't really need to consider search params if path changes
		if(location.pathname === prevLocation.pathname) {
			// Add other search parameters whose change should be included in page navigation calculation
			let whiteList = ['category'];
			let prevSearchParams = new URLSearchParams(prevLocation.search);
			let searchParams = new URLSearchParams(location.search);

			// Using for so can abort
			for(let i=0; i<whiteList.length; i++) {
				let paramKey = whiteList[i];
				if(searchParams.get(paramKey) !== prevSearchParams.get(paramKey)) {
					return true;
				}
			}
		} else {
			return true;
		}

		return false;
	}

	resetFocus = () => {
		document.body.focus();
	}

	resetScrollPosition() {
		window.scrollTo({top: 0});
	}

	loadUserSuccessful(user) {
		// reset console to show logs if live and ghosting
		if (Constants.ENV.IS_LIVE && user.ghosting) {
			// eslint-disable-next-line no-native-reassign, no-global-assign
			console = {...originalConsole};
		}

		this.updateUser(user);
		this.loadCsm(user);
		this.removeBgMask();
		
		console.log("initialize pendo user");
		PendoHelper.initialize(user);
	}

	maintenanceMode() {
		this.showBanner({content: 'App is in Maintenance mode'});
		this.removeLoadingIndicator();
	}
	
	loadUserFailed() {
		//this.removeBgMask();
		this.setState({unauthenticated: true});
	}

	loadCsm(user) {
		let poodle = new Fido();
		poodle.fetch(`/accounts/${user.accountId}/csm`)
			.then((data) => {
				if (data) {
					// TODO: in future, refactor to do as a context since it's used in multiple places?
					this.setState({csm: data});
				}
			})
			// TODO: probably only want to show some errors as snackbars
			.catch((error) => this.showSnackbar(error.message));
	}

	iOSDlgCloseHandler() {
		this.setState({showIOSInstallDlg: false});
	}

	// only platforms that support beforeinstallprompt
	handleInstallPrompt(event) {
		/*console.info('handleInstallPrompt');
		if(window.matchMedia('(display-mode: standalone)').matches) {
			console.info('chrome: (display-mode: standalone) test is true');
		}*/

		// Prevent Chrome <= 67 from automatically showing the prompt
		event.preventDefault();

		// Stash the event so it can be triggered later.
		this.installPromptEvent = event;

		// Update the install UI to notify the user app can be installed
		this.setState({'installable': true});
	}

	handleInstallBtn(event) {
		// only platforms that support beforeinstallprompt
		if(this.installPromptEvent) {
			// Show the modal add to home screen dialog
			this.installPromptEvent.prompt();
			// Wait for the user to respond to the prompt
			this.installPromptEvent.userChoice.then((choice) => {
				/*if (choice.outcome === 'accepted') {
					console.log('User accepted the A2HS prompt');
				} else {
					console.log('User dismissed the A2HS prompt');
				}*/
				// Clear the saved prompt since it can't be used again
				this.installPromptEvent = null;
				this.setState({'installable': false});
			});
		// platforms that do NOT support beforeinstallprompt (currently iOS < 11.3)
		} else if(Constants.ENV.IS_IOS && !window.navigator.standalone) {
			this.setState({showIOSInstallDlg: true});
		}
	}

	updateNetworkStatus() {
		if('onLine' in navigator) {
			if(!navigator.onLine) {
				this.showBanner({content: this.messages.networkOffline});
			} else if(navigator.onLine) {
				this.showBanner(false);
			}
		}
	}

	updateUser (user) {
		let obj = {authorized: user};
		if ("name" in user && user.name === "Error" && user.message.indexOf('401 Unauthorized') > -1) {
			obj.user = false;
			obj.unauthenticated = true;
		}
		this.setState(obj);
	}

	showBgMask() {
		document.getElementById("backgroundMask").style.display = "block";
	}

	removeBgMask() {
		document.getElementById("backgroundMask").style.display = "none";
	}

	removeLoadingIndicator() {
		document.getElementById("LoadingIndicator").style.display = "none";
	}

	logoutCallback() {
		this.showBanner(false);
		this.evictUser();
		this.showBgMask();
	}

	evictUser() {
		let {authorized} = this.state;

		this.setState({authorized: false});
	}

	updatePageTitle(title) {
		return title && typeof title === 'string' &&
			(window.document.title = `Detect - ${this.titleCase(title)}`);
	}

	titleCase(text) {
		return text.split(' ').map(word => {
			// Pretty rough, but not sure it'll matter - if word is less than 3 chars, don't upper case - a, of, etc;
			// might be better to switch to < 4, but not sure anyone will ever notice
			if(word.length < 3) {
				return word;
			} else {
				return word[0].toUpperCase() + word.slice(1);
			}
		}).join(' ');
	}

	/**
	 * Callback used to override header links, title, & back button. Any component that sets them
	 * is responsible for reseting them in `componentWillUnmount`.
	 * NOTE: make sure to call resetHeader (in componentWillUnmount) in any component that calls updateHeader
	 * @param  {Object} options Options used to update HeaderBar
	 */
	updateHeader (options) {
		if (options && options.title) {
			this.updatePageTitle(options.title);
		}

		this.setState(options);
	}

	/**
	 * Callback used to reset headerbar values to standard values
	 * NOTE: make sure to call this (in componentWillUnmount) in any component that calls updateHeader
	 */
	resetHeader () {
		this.updateHeader({
			signInUrl: null,
			backUrl: null,
			title: null,
			pageTitle: Constants.PAGE_TITLE,
			primary: true,
			hideHeaderbar: false,
			hideMargin: false
		});
	}

	/**
	 * Can pass in message string or json object with message, action, duration values
	 */
	showSnackbar(message) {
		let action, duration;
		if(typeof message === "object") {
			action = message.action || null;
			duration = message.duration || null;
			message = message.message;
		}

		// if duration false, snackbar remains until manually closed
		if(duration !== false) {
			duration = duration || this.snackbarInfo.defaultDuration;
		} else {
			duration = null;
		}

		if(message) {
			let id = this.generateSnackbarId();
			this.addToQueue({
				id: id,
				snackbarMessage: message,
				snackbarDuration: duration,
				snackbarAction: action,
				snackbarOpen: true
			});

			this.processQueue();
			return id;
		// if message is false, snackbar will be closed
		} else {
			this.setState({snackbarOpen: false});
			this.snackbarInfo.queueProcessing = false;
		}
	}

	generateSnackbarId() {
		return IdGenerator.getId();
	}

	// msgObj is an object {id: <string>, msg: <string>}
	updateSnackbarMessage(msgObj) {
		if (this.snackbarInfo.currentConfig && this.snackbarInfo.currentConfig.id === msgObj.id) {
			let sb = this.snackbarInfo.snackbarRef.current,
				sbInnerEl = sb ? (sb.querySelector("div")).querySelector("div") : null;

			if (sbInnerEl) {
				sbInnerEl.innerText = msgObj.msg;
			}
		}
	}

	addToQueue(config) {
		if(typeof config === "object") {
			this.snackbarInfo.queue.push(config);
		}
	}

	processQueue() {
		if(this.snackbarInfo.queue.length && !this.snackbarInfo.queueProcessing) {
			this.snackbarInfo.currentConfig = this.snackbarInfo.queue.shift();
			this.snackbarInfo.queueProcessing = this.snackbarInfo.currentConfig.snackbarOpen;
			this.setState(this.snackbarInfo.currentConfig);
		}
	}

	snackbarHandleExited () {
		// if more in queue, send to snackbar
		this.processQueue();
	}

	snackbarHandleEntering () {
		if (this.snackbarInfo.snackbarRef.current && this.snackbarInfo.snackbarRef.current.offsetHeight > 55) {
			this.setState({tallSnackbar: true});
			//this.setState({tallSnackbar: this.snackbarInfo.snackbarRef.current.offsetHeight});
		}
	}

	snackbarHandleClose (event, reason) {
		// other option is "timeout" (autoHideDuration expired)
		if (reason !== "clickaway" && this.snackbarInfo.currentConfig != null && this.snackbarInfo.currentConfig.disableClickAway !== true) {
			this.snackbarInfo.queueProcessing = false;
			this.setState({snackbarOpen: false});
			this.snackbarInfo.currentConfig = null;
		}
	}

	appRefreshSnackbar(registration, worker) {
		this.showSnackbar({
			duration: 15000,
			disableClickAway: true,
			message: this.messages.appUpdateAvailable,
			action: (
				<Button size="small" onClick={(event) => {
					if (worker) {
						worker.postMessage({ type: 'SKIP_WAITING' });
					}
					this.showSnackbar(false);
					window.location.reload();
				}} color="primary">Refresh</Button>
			)
		});
	}

	// only iOS
	shouldShowIOSInstallDlg() {
		let installableCount = localStorage.getItem("installable-count") || 0;
		if (this.state.authorized) {
			if (!localStorage.getItem("installable-dialog-shown")) {
				if (installableCount < 2) {
					localStorage.setItem("installable-count", ++installableCount);
				} else {
					localStorage.setItem("installable-dialog-shown", true);
					return true;
				}
			}
		}
		return false;
	}

	// config = {icon: {icon:[], size: "", className: "", title: ""}, content: "", actions (optional): []}
	// if config is false, banner disappears
	showBanner(config) {
		this.setState({
			banner: config
		});
	}

	setBannerClassName(classname) {
		this.setState({
			bannerClassName: classname
		});
	}

	bannerHandleEntering () {
		if (this.bannerRef.current) {
			this.setState({
				bannerHeight: 1
			});
		}
	}

	bannerHandleEntered () {
		if (this.bannerRef.current) {
			this.setState({
				bannerHeight: this.bannerRef.current.offsetHeight
			});
		}
	}

	bannerHandleExiting () {
		if (this.bannerRef.current) {
			this.setState({
				bannerHeight: this.state.bannerHeight
			});
		}
	}

	bannerHandleExited () {
		if (this.bannerRef.current) {
			this.setState({
				bannerHeight: 0
			});
		}
	}

	spaRedirect(path) {
		this.props.history.push(path);
	}

	/*signalsNudgeExited() {
		this.setState({signalsNudgeBannerExited: true});
	}*/

	render() {
		let {history, location} = this.props,
			{title, primary, installable, showIOSInstallDlg, backUrl, signInUrl,
				hideHeaderbar, hideMargin, banner, snackbarAction, snackbarOpen,
				snackbarMessage, snackbarDuration, tallSnackbar, authorized, unauthenticated, csm,
				bannerClassName, bannerHeight, previousLocation, showUseSignalsBanner, showBackToBanner, signalsNudgeBannerExited} = this.state;

		return (
			<AuthorizedUserContext.Provider value={authorized}>
				<JotaiProvider>
					<FullScreen
						csm={csm}
						title={title}
						primary={primary}
						backUrl={backUrl}
						signInUrl={signInUrl}
						hideHeaderbar={hideHeaderbar}
						hideMargin={hideMargin}
						installable={installable}
						showIOSInstallDlg={showIOSInstallDlg}
						iOSDlgCloseHandler={this.iOSDlgCloseHandler}
						handleInstallBtn={this.handleInstallBtn}
						snackbarRef={this.snackbarInfo.snackbarRef}
						snackbarOpen={snackbarOpen}
						tallSnackbar={tallSnackbar}
						snackbarAction={snackbarAction}
						snackbarMessage={snackbarMessage}
						snackbarDuration={snackbarDuration}
						showSnackbar={this.showSnackbar}
						snackbarHandleEntering={this.snackbarHandleEntering}
						snackbarHandleExited={this.snackbarHandleExited}
						snackbarHandleClose={this.snackbarHandleClose}
						banner={banner}
						bannerClassName={bannerClassName}
						bannerRef={this.bannerRef}
						bannerHeight={bannerHeight}
						bannerHandleEntering={this.bannerHandleEntering}
						bannerHandleEntered={this.bannerHandleEntered}
						bannerHandleExiting={this.bannerHandleExiting}
						bannerHandleExited={this.bannerHandleExited}
						showBanner={this.showBanner}
						history={history}
						location={location}
						locationPath={location.pathname}
						backToNext={this.backToNext}
						showUseSignalsBanner={showUseSignalsBanner}
						showBackToBanner={showBackToBanner}
						//signalsNudgeExited={this.signalsNudgeExited}
						signalsNudgeBannerExited={signalsNudgeBannerExited}
						unauthenticated={unauthenticated}>
						<AppRouter
							csm={csm}
							history={history}
							location={location}
							accessDeniedMsg={this.messages.accessDenied}
							noAccessMsg={this.messages.noAccess}
							somethingWrongMsg={this.messages.somethingWrong}
							pageNotFoundMsg={this.messages.pageNotFound}
							updateHeader={this.updateHeader}
							resetHeader={this.resetHeader}
							previousLocation={previousLocation}
							showSnackbar={this.showSnackbar}
							loginCallback={this.loadUserSuccessful}
							loadUserFailed={this.loadUserFailed}
							logoutCallback={this.logoutCallback}
							unauthenticated={unauthenticated}
							evictUser={this.evictUser}
							updateSnackbarMessage={this.updateSnackbarMessage}
							setBannerClassName={this.setBannerClassName}
							banner={banner}
							bannerHeight={bannerHeight}
							showBanner={this.showBanner}
							updateUser={this.updateUser}
							removeBgMask={this.removeBgMask} />
					</FullScreen>
				</JotaiProvider>
			</AuthorizedUserContext.Provider>
		);
	}
}

App.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired
};

export default withRouter(App);
